import React from "react";
import { ProfileIcon } from "@/common/input/SelectBoxFromApi";
import TruncateOnOverflow from "@/common/ui/TruncateOnOverflow";
import { TruncatedTextLinkWithPopup } from "@/common/ui/TruncatedTextLink";
import { getStatusColor, formateCurrency } from "@/utils/helper/string";
import moment from "moment";
import SensitiveField from "@/components/common/SensitiveField";

const RenderColumn = React.memo(({ type, row, column }: any) => {
  const currentWidth = column.getSize();

  let cellValue;
  try {
    cellValue = row.original[column.id];

    if (!cellValue) {
      return <TruncateOnOverflow value="-" width={currentWidth} className="font-normal !leading-normal text-13" />;
    }

    switch (type) {
      case "profile": {
        const { label, profilePic, name, profileText } = cellValue || {};
        const profileLabel = label || name;
        if (!profileText || !profileLabel || label == "-") return <span className="font-normal !leading-normal text-13 text-red-500">-</span>;
        return (
          <div className="flex items-center gap-1">
            {profilePic ? (
              <img
                src={profilePic}
                alt={profileLabel}
                className="h-5 w-5 rounded-full object-cover object-center"
                style={{ minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }}
              />
            ) : (
              <ProfileIcon className="!border" text={profileText} />
            )}
            <TruncateOnOverflow value={profileLabel} width={currentWidth} className="font-normal !leading-normal text-13" />
          </div>
        );
      }
      case "lookup": {
        if (column.id === "status") {
          return (
            <span className={`rounded-full text-13 whitespace-nowrap ${column.id === "status" ? getStatusColor(cellValue?.label) : ""}`}>
              {cellValue?.label || "-"}
            </span>
          );
        }
        return (
          <span className={`rounded-full text-13 whitespace-nowrap`}>
            <TruncateOnOverflow value={cellValue?.label || "-"} width={currentWidth} />
          </span>
        );
      }
      case "date-time": {
        const dateTime = moment(cellValue).format("DD-MMM-YYYY h:mm A");
        return <TruncateOnOverflow value={dateTime} width={currentWidth} className="font-normal !leading-normal text-13" />;
      }
      case "date": {
        const dateTime = moment(cellValue).format("DD-MMM-YYYY");
        return <TruncateOnOverflow value={dateTime} width={currentWidth} className="font-normal !leading-normal text-13" />;
      }
      case "link":
        return (
          <TruncatedTextLinkWithPopup
            link={{ text: cellValue || "-", value: cellValue || "#" }}
            className="text-13 !p-0 text-primary underline break-all truncate line-clamp-2 max-w-[200px] xl:max-w-[250px]"
          />
        );
      case "address":
        return <TruncateOnOverflow value={row.original[column.id] || "-"} width={currentWidth} className="font-normal !leading-normal text-13" />;
      default:
        // Handle sensitive fields (CTC, salary-related fields)
        if (column.id === "ctc" || column.id === "lastDrawnSalary" || column.id?.toLowerCase().includes("salary")) {
          if (!cellValue || cellValue === "-") {
            return <TruncateOnOverflow value="-" width={currentWidth} className="font-normal !leading-normal text-13" />;
          }
          // Format currency value
          const formatCurrencyValue = (val: any) => {
            if (Array.isArray(val) && val.length > 0) {
              // Convert array to string for formateCurrency
              const amountStr = typeof val[1] === "number" ? String(val[1]) : (typeof val[1] === "string" ? val[1] : String(val));
              const currencyValue = formateCurrency(amountStr);
              return `${val[0] || ""} ${currencyValue}`.trim();
            }
            return typeof val === "string" || typeof val === "number" ? String(val) : "-";
          };
          return (
            <SensitiveField
              value={cellValue}
              renderValue={(val) => (
                <TruncateOnOverflow
                  value={formatCurrencyValue(val)}
                  width={currentWidth - 30}
                  className="font-normal !leading-normal text-13"
                />
              )}
              className="font-normal !leading-normal text-13"
              minWidth={`${Math.max(currentWidth - 40, 100)}px`}
            />
          );
        }
        return (
          <TruncateOnOverflow
            value={cellValue !== undefined && cellValue !== null ? cellValue : "-"}
            width={currentWidth}
            className="font-normal !leading-normal text-13"
          />
        );
    }
  } catch (error) {
    return <span className="font-normal !leading-normal text-13 text-red-500">-</span>;
  }
});

RenderColumn.displayName = "RenderColumn";

export default RenderColumn;
